import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  normalizeLocale,
  resolveLocaleFromUrl,
  updateSearchWithLocale,
  writeStoredLocale,
} from '@gift-i18n';
import {
  clearPendingLocaleTransition,
  getPendingLocaleTransition,
  matchesPendingLocaleTransition,
} from './localeTransition';

function debugLocaleSync(event: string, payload: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;
  console.debug(`[i18n:web] ${event}`, payload);
}

export function useLocaleSync(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const fromUrl = resolveLocaleFromUrl(location.search);
    const activeLocale =
      normalizeLocale(i18n.resolvedLanguage || i18n.language) || DEFAULT_LOCALE;
    const pendingLocale = getPendingLocaleTransition();

    if (pendingLocale) {
      if (matchesPendingLocaleTransition(activeLocale, location.search)) {
        clearPendingLocaleTransition();
      } else {
        return;
      }
    }

    if (!fromUrl) return;
    if (fromUrl === activeLocale) return;

    debugLocaleSync('apply-url-locale', {
      fromUrl,
      activeLocale,
      search: location.search,
    });
    void i18n.changeLanguage(fromUrl);
  }, [i18n, i18n.language, i18n.resolvedLanguage, location.search]);

  useEffect(() => {
    const activeLocale =
      normalizeLocale(i18n.resolvedLanguage || i18n.language) || DEFAULT_LOCALE;
    writeStoredLocale(window.localStorage, activeLocale);
    document.documentElement.lang = activeLocale;
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    const fromUrl = resolveLocaleFromUrl(location.search);
    if (fromUrl) return;

    const activeLocale =
      normalizeLocale(i18n.resolvedLanguage || i18n.language) || DEFAULT_LOCALE;
    const pendingLocale = getPendingLocaleTransition();
    if (pendingLocale) {
      if (matchesPendingLocaleTransition(activeLocale, location.search)) {
        clearPendingLocaleTransition();
      } else {
        return;
      }
    }

    if (activeLocale === DEFAULT_LOCALE) return;

    const nextSearch = updateSearchWithLocale(location.search, activeLocale);
    if (nextSearch === location.search) return;

    debugLocaleSync('inject-missing-url-locale', {
      activeLocale,
      fromSearch: location.search,
      toSearch: nextSearch,
    });
    navigate(
      { pathname: location.pathname, search: nextSearch, hash: location.hash },
      { replace: true },
    );
  }, [
    i18n.language,
    i18n.resolvedLanguage,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);
}
